// SPDX-License-Identifier: AGPL-3.0-or-later

import {LimitResolver} from '@app/features/app/utils/LimitResolverAdapter';
import {isLimitToggleEnabled} from '@app/features/app/utils/LimitUtils';
import Channels from '@app/features/channel/state/Channels';
import {getVoiceConnectionContextFromMediaEngine} from '@app/features/voice/engine/VoiceMediaEngineBridge';

export interface VideoQualityProfile {
	maxHeight: number;
	maxFrameRate: number;
	profiles?: ReadonlyArray<{maxHeight: number; maxFrameRate: number}>;
}

export function resolveVideoQualityProfile(
	personalEntitled: boolean,
	guildProfile?: VideoQualityProfile,
): VideoQualityProfile {
	const personal = {maxHeight: personalEntitled ? 2160 : 720, maxFrameRate: personalEntitled ? 60 : 30};
	if (!guildProfile || guildProfile.maxFrameRate < 15) return personal;
	if (personal.maxHeight >= guildProfile.maxHeight && personal.maxFrameRate >= guildProfile.maxFrameRate)
		return personal;
	if (guildProfile.maxHeight >= personal.maxHeight && guildProfile.maxFrameRate >= personal.maxFrameRate)
		return guildProfile;
	return {
		maxHeight: Math.max(personal.maxHeight, guildProfile.maxHeight),
		maxFrameRate: Math.max(personal.maxFrameRate, guildProfile.maxFrameRate),
		profiles: [personal, guildProfile],
	};
}

export function hasHigherVideoQuality(): boolean {
	return isLimitToggleEnabled(
		{
			feature_higher_video_quality: LimitResolver.resolve({
				key: 'feature_higher_video_quality',
				fallback: 0,
				context: {guildId: null},
			}),
		},
		'feature_higher_video_quality',
	);
}

// TEMPORARY: modified clients can bypass this cap; enforce profiles through Fluxer's RPC/SFU publish policy
// on the first abuse or unwanted SFU egress finding, or at 80% of the egress budget, before claiming ten million concurrent users.
export function getEffectiveVideoQualityProfile(): VideoQualityProfile {
	const voice = getVoiceConnectionContextFromMediaEngine();
	const channel = voice?.channelId ? Channels.getChannel(voice.channelId) : undefined;
	const guildId =
		voice &&
		!voice.disconnecting &&
		(voice.connected || voice.connecting || voice.reconnecting) &&
		voice.guildId &&
		channel?.guildId === voice.guildId &&
		channel.isGuildVoice()
			? voice.guildId
			: null;
	const guildProfile = guildId
		? {
				maxHeight: LimitResolver.resolve({
					key: 'max_guild_video_height',
					fallback: 720,
					context: {guildId, traits: []},
					evaluationContext: 'guild',
				}),
				maxFrameRate: LimitResolver.resolve({
					key: 'max_guild_video_fps',
					fallback: 30,
					context: {guildId, traits: []},
					evaluationContext: 'guild',
				}),
			}
		: undefined;
	return resolveVideoQualityProfile(hasHigherVideoQuality(), guildProfile);
}
